/**
 * Stock file format checks, run in the browser before uploading so a wrong
 * file fails instantly with a short message instead of a round trip.
 *
 * The rules mirror the backend's (POST /dashboard/stockUpload): it needs an
 * Item Code, an MRP and a stock column, and rejects files with no rows. The
 * backend stays the source of truth — shortUploadError() shortens its
 * messages for anything this check lets through.
 */
import * as XLSX from "xlsx";

const normalize = (value) =>
  String(value ?? "")
    .replace(/^﻿/, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const REQUIRED_COLUMNS = [
  { label: "Item Code", names: ["item code"] },
  { label: "MRP", names: ["mrp"] },
  // The backend accepts any of these as the stock column.
  { label: "Current Stock", names: ["current stock", "closing stk", "supplier current stock", "stock"] },
];

export const REQUIRED_COLUMNS_LABEL = REQUIRED_COLUMNS.map((column) => column.label).join(", ");

// Exports sometimes carry a title or date line above the headers.
const HEADER_SCAN_ROWS = 10;
// Enough of a CSV to hold the header and a few rows.
const CSV_PEEK_BYTES = 64 * 1024;

const readRows = async (file) => {
  const isCsv = file.name.toLowerCase().endsWith(".csv");
  const data = isCsv ? await file.slice(0, CSV_PEEK_BYTES).text() : await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: isCsv ? "string" : "array", sheetRows: HEADER_SCAN_ROWS + 1 });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  // Keep blank rows: the backend treats a blank first row as an unreadable file.
  return XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: true, defval: "" });
};

// .xlsx files are zip archives ("PK"). .xls is left alone: some systems
// export HTML or text under that extension.
const isZip = async (file) => {
  const bytes = new Uint8Array(await file.slice(0, 2).arrayBuffer());
  return bytes[0] === 0x50 && bytes[1] === 0x4b;
};

const hasValues = (row) => row.some((cell) => String(cell).trim() !== "");

const missingColumns = (row) => {
  const headers = new Set(row.map(normalize));
  return REQUIRED_COLUMNS.filter((column) => !column.names.some((name) => headers.has(name))).map(
    (column) => column.label
  );
};

/** → null when the file looks right, else a short message to show the user. */
export async function checkStockFile(file) {
  if (!file.size) return "This file is empty.";
  if (file.name.toLowerCase().endsWith(".xlsx") && !(await isZip(file))) {
    return "This isn't a real .xlsx file. Export it again from your stock system.";
  }

  let rows;
  try {
    rows = await readRows(file);
  } catch (e) {
    return "This file couldn't be read. Export it again as .xlsx, .xls or .csv.";
  }
  if (!rows.some(hasValues)) return "This file is empty.";

  // The backend only reads the first row as headers.
  const missing = missingColumns(rows[0]);
  if (missing.length) {
    const headersLower = rows.slice(1, HEADER_SCAN_ROWS).some((row) => !missingColumns(row).length);
    return headersLower ? "Column headers must be on the first row. Remove the rows above them." : missingMessage(missing);
  }
  if (!rows.slice(1).some(hasValues)) return "This file has headers but no stock rows.";
  return null;
}

const missingMessage = (labels) =>
  labels.length === 1 ? `Missing column: ${labels[0]}.` : `Missing columns: ${labels.join(", ")}.`;

/**
 * Shortens the backend's upload errors, e.g. "Required column(s) not found:
 * Item Code, MRP, Current Stock / Closing Stk / Supplier Current Stock. This
 * file doesn't look like…" → "Missing columns: Item Code, MRP, Current Stock."
 */
export function shortUploadError(message) {
  if (!message) return "Stock file upload failed. Please try again.";
  const columns = message.match(/required column\(s\) not found:\s*(.+?)\.(\s|$)/i);
  if (columns) {
    // "A / B / C" lists alternatives for one column; show the first.
    return missingMessage(columns[1].split(",").map((name) => name.split("/")[0].trim()));
  }
  if (/empty or could not be read/i.test(message)) {
    return "This file is empty or couldn't be read.";
  }
  // Otherwise keep the backend's first sentence.
  const [sentence] = message.split(". ");
  return sentence.endsWith(".") ? sentence : `${sentence}.`;
}
